/**
 * Centralized Role Management
 * 
 * This file contains all role definitions and role groups used throughout the application.
 * Use these constants in your route definitions and components to ensure consistency.
 */

// Individual Roles
export const ROLES = {
  ADMIN: 'admin',
  MANAGER: 'manager',
  ANALYST: 'analyst',
  SALES: 'sales',
  FINANCE: 'finance',
  VIEWER: 'viewer',
  NONE: 'noone'
}

// Role Groups (for common access patterns)
export const ROLE_GROUPS = {
  // Full management access
  MANAGEMENT: [ROLES.ADMIN, ROLES.MANAGER],
  
  // Can view and create reports
  REPORTS_ACCESS: [ROLES.ADMIN, ROLES.MANAGER, ROLES.ANALYST],
  
  // Can access sales data and analytics
  SALES_ACCESS: [ROLES.ADMIN, ROLES.MANAGER, ROLES.SALES],
  
  // Can access financial data
  FINANCE_ACCESS: [ROLES.ADMIN, ROLES.FINANCE],
  
  // Can access statistics and analytics
  STATS_ACCESS: [ROLES.ADMIN, ROLES.MANAGER, ROLES.ANALYST, ROLES.SALES],
  
  // All authenticated users
  ALL_USERS: [ROLES.ADMIN, ROLES.MANAGER, ROLES.ANALYST, ROLES.SALES, ROLES.FINANCE, ROLES.VIEWER]
}

/**
 * Check if a user has a specific role
 * @param {string} userRole - The user's role
 * @param {string} requiredRole - The required role
 * @returns {boolean}
 */
export function hasRole(userRole, requiredRole) {
  return userRole === requiredRole
}

/**
 * Check if a user has any of the specified roles
 * @param {string} userRole - The user's role
 * @param {string[]} requiredRoles - Array of acceptable roles
 * @returns {boolean}
 */
export function hasAnyRole(userRole, requiredRoles) {
  return requiredRoles.includes(userRole)
}

/**
 * Check if a user is an admin
 * @param {string} userRole - The user's role
 * @returns {boolean}
 */
export function isAdmin(userRole) {
  return userRole === ROLES.ADMIN
}

/**
 * Check if a user has management privileges
 * @param {string} userRole - The user's role
 * @returns {boolean}
 */
export function isManagement(userRole) {
  return ROLE_GROUPS.MANAGEMENT.includes(userRole)
}

/**
 * Get a human-readable display name for a role
 * @param {string} role - The role identifier
 * @returns {string}
 */
export function getRoleDisplayName(role) {
  const displayNames = {
    [ROLES.ADMIN]: 'Administrator',
    [ROLES.MANAGER]: 'Manager',
    [ROLES.ANALYST]: 'Analyst',
    [ROLES.SALES]: 'Sales',
    [ROLES.FINANCE]: 'Finance',
    [ROLES.VIEWER]: 'Viewer',
    [ROLES.NONE]: 'No Role'
  }
  return displayNames[role] || role
}

export default {
  ROLES,
  ROLE_GROUPS,
  hasRole,
  hasAnyRole,
  isAdmin,
  isManagement,
  getRoleDisplayName
}

